/**
 * ScribeClickDataParser
 *
 * This module is responsible for parsing click data from an element.
 *
 * @module    scribe_click_data_parser
 * @author    Brian LaMattina <blamattina@wayfair.com>
 * @copyright 2016 Wayfair, LLC - All rights reserved
 */
/* eslint-env browser */

import _ from 'underscore';

const getDataAttr = function(node, attr) {
  const actualNode = node.jquery ? node[0] : node;
  return actualNode && actualNode.getAttribute('data-' + attr);
};

/**
 * Construct a ScribeClickDataParser
 *
 * The clicked element should not be detached.  It will not be modified.
 *
 * @class
 * @param {jQuery} $el clicked element
 */
export default class ScribeClickDataParser {
  CLICK_LOCATION = 'click-location';
  CLICK_METADATA = 'click-location-metadata';
  CMS_LINK_ID = 'cms-linkid';
  CMS_LINK_CLASS = 'js-cms-link';
  CMS_LEGO_VERSION = 'lego-version';
  CMS_LEGO_SELECTOR = '[id^=cms_lego_]';
  CMS_PAGE_SELECTOR = '[id^=cms_page_]';
  CMS_CLICK_TAG = 'click-track';
  JS_TRACK_EVENT_CLASS = 'js-track-event';
  EVENT_NAME = 'event-name';
  DAILY_SALES_EVENT_PAGE = 'DailySalesEventPage';

  constructor(config, $el) {
    this.config = config;
    this.$el = $el;
  }

  static getOptionalData($el, attr) {
    // return the value of the passed attribute
    // if that is not present, return trimmed text contained inside the element
    // if somehow neither is possible, return an unknown-location string
    return getDataAttr($el, attr) || $el.text().trim() || 'unknown-location';
  }

  static getElementMarkup($el) {
    return $el
      .clone()
      .empty()
      .prop('outerHTML')
      .replace(/\s+/g, ' ');
  }

  static getRequiredData($el, attr) {
    const data = getDataAttr($el, attr);
    if (!data) {
      throw new Error(
        'Click Parser Error: Unable to parse required data ' +
          'attribute "data-' +
          attr +
          '" from: ' +
          this.getElementMarkup($el)
      );
    }
    return data;
  }

  static castInt(string, $el) {
    if (_.isNaN(+string)) {
      throw new Error(
        'Click Parser Error: expected "' +
          string +
          '" to be a ' +
          'number in: ' +
          this.getElementMarkup($el)
      );
    }
    return +string;
  }

  static findCmsId($el, selector) {
    const $cmsEl = $el.closest(selector);
    if (!$cmsEl.length) {
      throw new Error(
        'Click Parser Error: Unable find CMS Element using ' +
          ' selector "' +
          selector +
          '" close to: ' +
          this.getElementMarkup($el)
      );
    }

    const id = this.castInt($cmsEl.attr('id').match(/\d+/));
    if (!id) {
      throw new Error(
        'Click Parser Error: Unable to parse required ' +
          'attribute from CMS lego ID from: ' +
          this.getElementMarkup($cmsEl)
      );
    }
    return id;
  }

  /**
   * Parse click data.
   *
   * @return {Object} data attributes parsed from the link
   */
  parse() {
    const data = {
      clickLoc: this.constructor.getOptionalData(this.$el, this.CLICK_LOCATION),
      clickLocMeta: getDataAttr(this.$el, this.CLICK_METADATA),
      clickType: 'data-click-location',
      eventName: getDataAttr(this.$el, this.EVENT_NAME),
    };

    const isCmsLink = this.$el.hasClass(this.CMS_LINK_CLASS);
    const isJsTrackElement = this.$el.hasClass(this.JS_TRACK_EVENT_CLASS);
    if (isCmsLink) {
      const $lego = this.$el.closest(this.CMS_LEGO_SELECTOR);
      data.pid = this.constructor.findCmsId(this.$el, this.CMS_PAGE_SELECTOR);
      data.legoId = this.constructor.findCmsId(
        this.$el,
        this.CMS_LEGO_SELECTOR
      );
      data.linkId = this.constructor.getRequiredData(
        this.$el,
        this.CMS_LINK_ID
      );
      data.legoVid = this.constructor.castInt(
        this.constructor.getRequiredData($lego, this.CMS_LEGO_VERSION),
        $lego
      );
      data.clickTag = this.constructor.getOptionalData(
        this.$el,
        this.CMS_CLICK_TAG
      );
    } else if (isJsTrackElement) {
      data.clickType = 'js-track-event';
    }

    // Add image width and height in vw units if on Mobile Event Page and using Natural Aspect Ratio Image Size with Masonry
    if (
      this.config.pageType === this.DAILY_SALES_EVENT_PAGE &&
      this.config.isMobileWeb &&
      this.$el.parents().hasClass('DSMasonry-ProductBlock-imageWrap')
    ) {
      data.imageWidth = Math.round(
        (this.$el.width() / window.innerWidth) * 100
      );
      data.imageHeight = Math.round(
        (this.$el.height() / window.innerWidth) * 100
      );
    }

    // Omit falsy values
    return _.pick(data, _.identity);
  }
}
